import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import Header from './Header'

/**
 * Application layout component for authenticated/private pages
 * Features full-height sidebar that extends from top to bottom of screen
 * Includes header that only spans the main content area
 * Supports mobile sidebar overlay for navigation
 */
const AppLayout = ({ children }) => {
  const location = useLocation()
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)

  // Scroll lock when mobile sidebar is open for better UX on small screens
  useEffect(() => {
    document.body.classList.toggle('overflow-hidden', isMobileSidebarOpen)
    return () => document.body.classList.remove('overflow-hidden')
  }, [isMobileSidebarOpen])

  // Close mobile sidebar on Escape key for accessibility and usability
  useEffect(() => {
    if (!isMobileSidebarOpen) return
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsMobileSidebarOpen(false)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isMobileSidebarOpen])

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Full-height sidebar */}
      <Sidebar 
        fullHeight={true} 
        isCollapsed={isSidebarCollapsed}
        onToggle={(collapsed) => setIsSidebarCollapsed(collapsed)}
        isMobileOpen={isMobileSidebarOpen}
        onMobileToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
      />
      
      {/* Main content area with header */}
      <div className={`flex-1 flex flex-col transition-all duration-300 min-w-0 ${
        isSidebarCollapsed ? 'md:ml-14' : 'md:ml-60'
      }`}>
        {/* Header with mobile sidebar button */}
        <Header 
          appLayoutPage={true}
          showMobileSidebarButton={true}
          onMobileSidebarToggle={() => setIsMobileSidebarOpen(true)}
        />
        
        {/* Main content */}
        <main className="flex-1 min-w-0 p-4 md:p-6 lg:p-0 xl:p-8 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  )
}

export default AppLayout
